import _defineProperty from "/vercel/path1/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
import React from "react";
var __jsx = React.createElement;
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/button";
import { FormTextarea } from "@/components/form";
import { ActionCentre } from "@/components/action-centre";
import { useUpdateReminder } from "@/hooks/tasks";
import { useSelectedPatientStore } from "@/stores/selected-patient";
import { trackEvent, ANALYTICS_EVENTS } from "@/utils/analytics";
import { TaskDetailHeader } from "./task-detail-header";
import { TaskDetailMeta } from "./task-detail-meta";
import { TaskDetailComments } from "./task-detail-comments";
import { TaskDetailFooter } from "./task-detail-footer";
import { buildTaskEdits, isTaskEditsDirty } from "./task-edits";
import { fromDateInputValue } from "@/utils/date";
import { getSaveErrorMessage } from "./get-save-error-message";
/** Task detail panel: editable title, description and metadata sharing one save/discard. */
export function TaskDetail(_ref) {
  var reminder = _ref.reminder,
    onClose = _ref.onClose,
    onMinimize = _ref.onMinimize,
    onMarkDone = _ref.onMarkDone,
    _ref$isMarkingDone = _ref.isMarkingDone,
    isMarkingDone = _ref$isMarkingDone === void 0 ? false : _ref$isMarkingDone,
    onChangeContext = _ref.onChangeContext,
    onSaved = _ref.onSaved,
    _ref$embedded = _ref.embedded,
    embedded = _ref$embedded === void 0 ? false : _ref$embedded;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var initial = buildTaskEdits(reminder);
  var _useState = useState(initial),
    edits = _useState[0],
    setEdits = _useState[1];
  var _useState2 = useState(initial),
    committed = _useState2[0],
    setCommitted = _useState2[1];
  var _useState3 = useState(false),
    isEditingTitle = _useState3[0],
    setIsEditingTitle = _useState3[1];
  var _useState4 = useState(null),
    saveError = _useState4[0],
    setSaveError = _useState4[1];
  var patientUid = useSelectedPatientStore(function (s) {
    var _s$selectedPatient;
    return (_s$selectedPatient = s.selectedPatient) !== null && _s$selectedPatient !== void 0 && _s$selectedPatient.patient_uid ? String(s.selectedPatient.patient_uid) : null;
  });
  var _useUpdateReminder = useUpdateReminder(patientUid),
    updateReminder = _useUpdateReminder.mutate,
    isSaving = _useUpdateReminder.isLoading;
  var isDirty = isTaskEditsDirty(edits, committed);
  var patch = function patch(next) {
    return setEdits(function (prev) {
      return _objectSpread(_objectSpread({}, prev), next);
    });
  };
  var handleSave = function handleSave() {
    setSaveError(null);
    updateReminder({
      reminderId: reminder.id,
      payload: {
        title: edits.title,
        context: edits.description,
        remind_after: fromDateInputValue(edits.dueDate),
        owned_by: edits.ownedBy ? Number(edits.ownedBy) : null,
        label_ids: edits.labelId ? [Number(edits.labelId)] : [],
        linked_resources: edits.linkedResources
      }
    }, {
      onSuccess: function onSuccess(updatedReminder) {
        setCommitted(edits);
        setIsEditingTitle(false);
        onSaved === null || onSaved === void 0 || onSaved(updatedReminder);
      },
      onError: function onError(error) {
        return setSaveError(getSaveErrorMessage(error));
      }
    });
  };
  var handleDiscard = function handleDiscard() {
    setEdits(committed);
    setIsEditingTitle(false);
    setSaveError(null);
  };
  var content = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TaskDetailHeader, {
    reminder: reminder,
    title: edits.title,
    isEditing: isEditingTitle,
    onStartEdit: function onStartEdit() {
      return setIsEditingTitle(true);
    },
    onTitleChange: function onTitleChange(value) {
      return patch({
        title: value
      });
    },
    onRevertTitle: function onRevertTitle() {
      patch({
        title: committed.title
      });
      setIsEditingTitle(false);
    },
    onMinimize: onMinimize,
    onClose: onClose
  }), /*#__PURE__*/React.createElement("div", {
    className: "flex-1 overflow-y-auto scrollbar-sds px-5 py-5 space-y-5 min-h-0"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col gap-2"
  }, /*#__PURE__*/React.createElement("span", {
    className: "text-style-body-small-strong text-sds-base-default"
  }, t("label.task_description")), /*#__PURE__*/React.createElement(FormTextarea, {
    "data-testid": "task-detail-description",
    value: edits.description,
    minRows: 3,
    onChange: function onChange(e) {
      patch({
        description: e.currentTarget.value
      });
      onChangeContext === null || onChangeContext === void 0 || onChangeContext(e.currentTarget.value);
    },
    onBlur: function onBlur() {
      return trackEvent(ANALYTICS_EVENTS.TASK_DETAIL_DESCRIPTION, {
        taskId: reminder.id
      });
    }
  })), /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col gap-3"
  }, /*#__PURE__*/React.createElement(TaskDetailMeta, {
    reminderId: reminder.id,
    labelId: edits.labelId,
    ownedBy: edits.ownedBy,
    dueDate: edits.dueDate,
    linkedResources: edits.linkedResources,
    onLabelChange: function onLabelChange(value) {
      return patch({
        labelId: value
      });
    },
    onOwnerChange: function onOwnerChange(value) {
      return patch({
        ownedBy: value
      });
    },
    onDueDateChange: function onDueDateChange(value) {
      return patch({
        dueDate: value
      });
    },
    onLinkedChange: function onLinkedChange(value) {
      return patch({
        linkedResources: value
      });
    }
  }), isDirty ? /*#__PURE__*/React.createElement("div", {
    className: "flex items-center justify-end gap-3"
  }, saveError ? /*#__PURE__*/React.createElement("span", {
    className: "mr-auto text-style-body-small text-sds-urgent-secondary"
  }, saveError) : null, /*#__PURE__*/React.createElement(Button, {
    "data-testid": "task-detail-meta-discard",
    variant: "ghost",
    disabled: isSaving,
    onClick: function onClick() {
      trackEvent(ANALYTICS_EVENTS.TASK_DETAIL_META_DISCARD, {
        taskId: reminder.id
      });
      handleDiscard();
    }
  }, t("button.discard")), /*#__PURE__*/React.createElement(Button, {
    "data-testid": "task-detail-meta-save",
    variant: "primary",
    intent: "brand",
    loading: isSaving,
    onClick: function onClick() {
      trackEvent(ANALYTICS_EVENTS.TASK_DETAIL_META_SAVE, {
        taskId: reminder.id
      });
      handleSave();
    }
  }, isSaving ? t("saving") : t("button.save"))) : null), /*#__PURE__*/React.createElement(TaskDetailComments, {
    reminderId: reminder.id
  }), /*#__PURE__*/React.createElement(ActionCentre, {
    reminderId: reminder.id
  })), /*#__PURE__*/React.createElement(TaskDetailFooter, {
    reminder: reminder,
    onClose: onClose,
    onMarkDone: onMarkDone,
    isMarkingDone: isMarkingDone
  }));
  if (embedded) {
    return /*#__PURE__*/React.createElement("div", {
      className: "flex h-full flex-col overflow-hidden bg-sds-base-default"
    }, content);
  }
  return /*#__PURE__*/React.createElement("div", {
    className: "absolute inset-0 z-sds-1200 w-full pointer-events-auto"
  }, /*#__PURE__*/React.createElement("div", {
    className: "absolute inset-0",
    "data-testid": "task-detail-backdrop-close",
    onClick: function onClick() {
      trackEvent(ANALYTICS_EVENTS.TASK_DETAIL_CLOSE);
      onClose();
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: "absolute inset-4 flex flex-col bg-sds-base-default rounded-sds-400 shadow-sds-600 overflow-hidden"
  }, content));
}